/**
 * The converter.
 *
 * Holds the project being assembled - pages, floors, scales, recognition results - and routes
 * canvas clicks to whichever step asked for them. The steps themselves are in Sidebar; the plan
 * and its overlays are in PlanCanvas; the coordinate rules are in conversion-core and the state
 * rules in state.ts. This file is the wiring.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  epicMapFileName,
  expandDistrictAbbreviations,
  expandSchoolTypeAbbreviations,
  normalizeState,
  schoolNameFromFileName,
  type Point,
  type Size,
} from "@plan-forge/conversion-core";
import type { OutputType } from "@plan-forge/job-contract";
import { buildProjectPackage, packageFileName, type AreaPackage } from "@plan-forge/conversion-package";
import { buildEpicMap } from "./epic-map-render.ts";
import { PlanCanvas } from "./PlanCanvas.tsx";
import { Sidebar } from "./Sidebar.tsx";
import { openPlan, UnsupportedSourceError, type PlanPage, type PlanSource } from "./plan-source.ts";
import {
  ApiError,
  awaitJob,
  createJob,
  createPackage,
  fetchPackage,
  fetchResult,
  getJob,
  uploadPlan,
  type JobResponse,
  type RecognitionResult,
} from "./api.ts";
import { recognitionWarningOf } from "@plan-forge/conversion-package";
import { authHeader, authRequired, initialAuthState, signIn, signOut, type AuthState } from "./auth.ts";
import { deleteProject, listProjects, loadProject, saveProject } from "./project-store.ts";
import { newProjectId, restoreState, type ProjectSummary } from "./project-snapshot.ts";
import { canChooseSaveLocation, chooseSaveTarget, type SaveTarget } from "./save-file.ts";
import { SignIn } from "./SignIn.tsx";
import {
  CLOSE_RADIUS,
  EMPTY_AREA,
  INITIAL_STATE,
  MAX_FLOORS_PER_PAGE,
  MIN_POLYGON_POINTS,
  areaDraft,
  areaKey,
  areasInPlay,
  areasToRecognise,
  blockedReason,
  effectiveScale,
  floorIndexes,
  packageBlockedReason,
  pageSetup,
  type AreaDraft,
  type AreaRef,
  type CanvasMode,
  type ConversionState,
  type CreatedPackage,
  type JobPhase,
  type PageSetup,
  type SchoolDetails,
} from "./state.ts";

/** Two clicks this close are one click that became a double-click, not two vertices. */
const DUPLICATE_POINT = 2;

/**
 * Drops a finished package. Spread into any change to a polygon or a scale: a download that
 * was built from the old configuration would otherwise stay on screen looking current.
 */
const CLEARED_PACKAGE = {
  packagePhase: "none",
  createdPackages: [],
} as const satisfies Partial<ConversionState>;

const phaseOf = (status: JobResponse["status"]): JobPhase => (status === "queued" ? "queued" : status);

export function App() {
  const [state, setStateRaw] = useState<ConversionState>(INITIAL_STATE);
  /**
   * The latest state, including changes React has not rendered yet.
   *
   * Clicks on the plan decide what to do from the state before them - a click near the first
   * vertex closes the shape - and on a heavy sheet a render can lag behind the next click, so
   * they have to read this rather than the last rendered value.
   */
  const stateRef = useRef(state);
  stateRef.current = state;
  const setState = useCallback((next: ConversionState | ((current: ConversionState) => ConversionState)) => {
    const resolved = typeof next === "function" ? next(stateRef.current) : next;
    stateRef.current = resolved;
    setStateRaw(resolved);
  }, []);
  const [source, setSource] = useState<PlanSource | null>(null);
  const [page, setPage] = useState<PlanPage | null>(null);
  const [mode, setMode] = useState<CanvasMode>("pan");
  const [zoom, setZoom] = useState(1);
  /** Recognised geometry by area. Too large for React state's comfort, so kept beside it. */
  const [results, setResults] = useState<Readonly<Record<string, RecognitionResult>>>({});
  const [showResult, setShowResult] = useState(true);
  const [uploadFraction, setUploadFraction] = useState<number | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const [problem, setProblem] = useState<string[] | null>(null);
  const [auth, setAuth] = useState<AuthState>({ kind: authRequired ? "loading" : "disabled" });
  const [projectId, setProjectId] = useState(newProjectId);
  const [projects, setProjects] = useState<readonly ProjectSummary[]>([]);
  const cancel = useRef<AbortController | null>(null);

  // The File is kept out of React state: it is inert, it is large, and putting it in state
  // means every keystroke in the details form re-renders against a 40 MB object.
  const sourceFile = useRef<File | null>(null);
  /** Uploaded once per plan. Every area's job names the same key. */
  const sourceKey = useRef<string | null>(null);
  const previews = useRef(new Map<number, Size>());
  /** Projects whose plan file is already in storage, so autosave writes it once. */
  const filesSaved = useRef(new Set<string>());

  // Runs once. Completes a redirect back from Entra, or resumes from a refresh token in this
  // tab, or settles on signed-out.
  useEffect(() => {
    if (!authRequired) return;
    void initialAuthState().then(setAuth);
  }, []);

  // Every call to our own API goes through this. It refreshes a token that is close to
  // expiring and hands the newer session back, so the next call is not still presenting the
  // old one.
  const headers = useCallback(
    () => authHeader(auth, (session) => setAuth({ kind: "signed-in", session, user: userOf(session) })),
    [auth],
  );

  // Releasing the PDF worker and the decoded bitmap matters here: a 6048x4320 sheet held after
  // the operator has moved to another plan is tens of megabytes of nothing.
  useEffect(() => () => source?.dispose(), [source]);

  const refreshProjects = useCallback(() => void listProjects().then(setProjects), []);
  useEffect(refreshProjects, [refreshProjects]);

  // Saved a moment after the last change rather than on every keystroke: the plan is a
  // multi-megabyte Blob and a details field fires an update per character.
  useEffect(() => {
    const file = sourceFile.current;
    if (source === null || file === null) return;
    const timer = setTimeout(
      () =>
        void saveProject(projectId, file, state, !filesSaved.current.has(projectId)).then((stored) => {
          if (stored) filesSaved.current.add(projectId);
          refreshProjects();
        }),
      800,
    );
    return () => clearTimeout(timer);
  }, [source, projectId, state, refreshProjects]);

  // ---- state helpers ---------------------------------------------------------------------

  const patch = useCallback(
    (changes: Partial<ConversionState>) => setState((current) => ({ ...current, ...changes })),
    [],
  );

  const updateArea = useCallback(
    (pageNumber: number, areaIndex: number, change: (area: AreaDraft) => AreaDraft, keepPackage = false) =>
      setState((current) => ({
        ...current,
        ...(keepPackage ? {} : CLEARED_PACKAGE),
        areas: { ...current.areas, [areaKey(pageNumber, areaIndex)]: change(areaDraft(current, pageNumber, areaIndex)) },
      })),
    [],
  );

  const updatePage = useCallback(
    (pageNumber: number, change: (setup: PageSetup) => PageSetup) =>
      setState((current) => ({
        ...current,
        ...CLEARED_PACKAGE,
        pages: { ...current.pages, [pageNumber]: change(pageSetup(current, pageNumber)) },
      })),
    [],
  );

  const previewOf = useCallback(
    async (pageNumber: number): Promise<Size> => {
      const known = previews.current.get(pageNumber);
      if (known !== undefined) return known;
      if (source === null) throw new Error("No plan is open.");
      const { preview } = await source.page(pageNumber);
      previews.current.set(pageNumber, preview);
      return preview;
    },
    [source],
  );

  // ---- following jobs --------------------------------------------------------------------

  /**
   * Follows one job to its end and records what came of it.
   *
   * Returns a message when the area failed, and never throws for a failed job: one bad area
   * should not discard the ones that finished, and the drafter retries only the failure.
   */
  const watchJob = useCallback(
    async (ref: AreaRef, jobId: string, outputType: OutputType, scale: number, polygon: readonly Point[], signal: AbortSignal): Promise<string | null> => {
      const label = `Page ${ref.pageNumber}, area ${ref.areaIndex + 1}`;
      try {
        const finished = await awaitJob(
          jobId,
          (update) => updateArea(ref.pageNumber, ref.areaIndex, (area) => ({ ...area, jobPhase: phaseOf(update.status), jobId }), true),
          signal,
          headers,
        );
        if (finished.status === "failed") {
          updateArea(ref.pageNumber, ref.areaIndex, (area) => ({ ...area, jobPhase: "failed", jobId: null }), true);
          return `${label}: ${finished.error ?? "recognition failed."}`;
        }
        const result = await fetchResult(finished.resultUrl!);
        setResults((current) => ({ ...current, [ref.key]: result }));
        setState((current) => ({
          ...current,
          ...CLEARED_PACKAGE,
          areas: {
            ...current.areas,
            [ref.key]: {
              ...areaDraft(current, ref.pageNumber, ref.areaIndex),
              jobPhase: "succeeded",
              jobId: null,
              run: { jobId, outputType, scale, polygon, sourceDocumentType: result.sourceDocumentType },
            },
          },
        }));
        setShowResult(true);
        return null;
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") return null;
        updateArea(ref.pageNumber, ref.areaIndex, (area) => ({ ...area, jobPhase: "failed", jobId: null }), true);
        return `${label}: ${error instanceof ApiError ? error.errors.join("; ") : (error as Error).message}`;
      }
    },
    [updateArea, headers],
  );

  /**
   * Brings a reopened project's results back, and rejoins jobs that were still running.
   *
   * A result that cannot be fetched - the job expired, or the session did - is dropped from the
   * area so it shows as needing recognition, rather than claiming a geometry nobody can see.
   */
  const rejoin = useCallback(
    async (restored: ConversionState) => {
      const controller = new AbortController();
      cancel.current = controller;
      await Promise.all(
        areasInPlay(restored).map(async (ref) => {
          const draft = areaDraft(restored, ref.pageNumber, ref.areaIndex);
          try {
            if (draft.run !== null) {
              const job = await getJob(draft.run.jobId, headers);
              if (job.status !== "succeeded" || job.resultUrl === undefined) throw new Error("gone");
              const result = await fetchResult(job.resultUrl);
              setResults((current) => ({ ...current, [ref.key]: result }));
            } else if (draft.jobId !== null) {
              const scale = effectiveScale(restored, ref.pageNumber);
              if (scale !== null) {
                const message = await watchJob(ref, draft.jobId, restored.outputType, scale.scale, draft.polygon, controller.signal);
                if (message !== null) setProblem((current) => [...(current ?? []), message]);
              }
            }
          } catch {
            updateArea(ref.pageNumber, ref.areaIndex, (area) => ({ ...area, jobPhase: "none", jobId: null, run: null }), true);
          }
        }),
      );
    },
    [headers, watchJob, updateArea],
  );

  // ---- opening a plan, and starting over --------------------------------------------------

  const open = useCallback(
    async (file: File, restored: ConversionState | null = null, id: string | null = null) => {
      setProblem(null);
      try {
        const opened = await openPlan(file);
        const startPage = restored === null ? 1 : Math.min(Math.max(1, restored.pageNumber), opened.pageCount);
        const first = await opened.page(startPage);
        source?.dispose();
        cancel.current?.abort();
        previews.current = new Map([[startPage, first.preview]]);
        sourceFile.current = file;
        sourceKey.current = null;
        setSource(opened);
        setPage(first);
        setResults({});
        setProgress(null);
        setMode("pan");
        setZoom(fitZoom(first.preview.width, first.preview.height));
        setProjectId(id ?? newProjectId());
        // Everything drawn on the old plan is meaningless on a new one, and silently keeping a
        // polygon across a file change is how somebody converts the wrong area.
        setState(
          restored !== null
            ? { ...restored, pageNumber: startPage, areaIndex: 0 }
            : {
                ...INITIAL_STATE,
                outputType: state.outputType,
                details: {
                  ...state.details,
                  schoolName:
                    state.details.schoolName === "" ? schoolNameFromFileName(file.name) : state.details.schoolName,
                },
              },
        );
        if (restored !== null) void rejoin(restored);
      } catch (error) {
        setProblem([
          error instanceof UnsupportedSourceError || error instanceof Error
            ? error.message
            : "That file could not be opened.",
        ]);
      }
    },
    [source, state.outputType, state.details, rejoin],
  );

  const handleFile = useCallback((file: File) => void open(file), [open]);

  const openProject = useCallback(
    async (id: string) => {
      const loaded = await loadProject(id);
      if (loaded === null) {
        setProblem(["That project could not be opened from this browser's storage."]);
        return;
      }
      const restored = restoreState(loaded.state);
      if (restored === null) {
        setProblem(["That project was saved in a format this version cannot read."]);
        return;
      }
      filesSaved.current.add(id);
      await open(loaded.file, restored, id);
    },
    [open],
  );

  const removeProject = useCallback(
    async (id: string) => {
      await deleteProject(id);
      refreshProjects();
    },
    [refreshProjects],
  );

  /**
   * The desktop application's New Project: everything cleared, the previous work not exported.
   * The previous project stays in the list, which the desktop application could not offer.
   */
  const newProject = useCallback(() => {
    cancel.current?.abort();
    source?.dispose();
    sourceFile.current = null;
    sourceKey.current = null;
    previews.current = new Map();
    setSource(null);
    setPage(null);
    setResults({});
    setProgress(null);
    setUploadFraction(null);
    setProblem(null);
    setMode("pan");
    setProjectId(newProjectId());
    setState(INITIAL_STATE);
  }, [source]);

  /** Counts navigations, so a slow page load that was overtaken by a newer click is dropped. */
  const navigation = useRef(0);

  const goToPage = useCallback(
    async (pageNumber: number) => {
      if (source === null) return;
      const clamped = Math.min(Math.max(1, pageNumber), source.pageCount);
      const ticket = ++navigation.current;
      const next = await source.page(clamped);
      if (ticket !== navigation.current) return;
      previews.current.set(clamped, next.preview);
      setPage(next);
      setMode("pan");
      setZoom(fitZoom(next.preview.width, next.preview.height));
      // Work on the page being left is kept: a page's scale and floors belong to it and
      // survive a visit to another.
      patch({ pageNumber: clamped, areaIndex: 0 });
    },
    [source, patch],
  );

  // ---- details ---------------------------------------------------------------------------

  const setDetails = useCallback((details: SchoolDetails) => patch({ details, ...CLEARED_PACKAGE }), [patch]);

  // ---- drawing ---------------------------------------------------------------------------

  const setFloorCount = useCallback(
    (pageNumber: number, count: number) => {
      const floorCount = Math.min(MAX_FLOORS_PER_PAGE, Math.max(1, Math.round(count)));
      updatePage(pageNumber, (setup) => ({ ...setup, floorCount }));
      setState((current) => (current.areaIndex >= floorCount ? { ...current, areaIndex: floorCount - 1 } : current));
    },
    [updatePage],
  );

  /** Closes the polygon, and moves on to the next floor on the page that has none yet. */
  const finishArea = useCallback(() => {
    setState((current) => {
      const { pageNumber, areaIndex } = current;
      const area = areaDraft(current, pageNumber, areaIndex);
      // A double-click arrives as two clicks first, so the last vertex is a repeat of the one
      // before it. Drop it rather than keep a zero-length edge.
      let polygon = area.polygon;
      const last = polygon.at(-1);
      const before = polygon.at(-2);
      if (last !== undefined && before !== undefined && Math.hypot(last.x - before.x, last.y - before.y) < DUPLICATE_POINT) {
        polygon = polygon.slice(0, -1);
      }
      if (polygon.length < MIN_POLYGON_POINTS) return current;

      const areas = { ...current.areas, [areaKey(pageNumber, areaIndex)]: { ...area, polygon, polygonClosed: true } };
      const next = { ...current, ...CLEARED_PACKAGE, areas };
      const undrawn = floorIndexes(next, pageNumber).find((i) => i !== areaIndex && !areaDraft(next, pageNumber, i).polygonClosed);
      return undrawn === undefined ? next : { ...next, areaIndex: undrawn };
    });
    setMode("pan");
  }, []);

  const placePoint = useCallback(
    (point: Point) => {
      if (mode === "calibrate") {
        updatePage(state.pageNumber, (setup) => {
          const { from, to } = setup.calibration;
          // Third click starts a fresh pair rather than silently doing nothing, which is what
          // somebody who mis-clicked the second point expects.
          const calibration =
            from === null || to !== null ? { ...setup.calibration, from: point, to: null } : { ...setup.calibration, to: point };
          return { ...setup, calibration };
        });
        return;
      }

      if (mode === "draw") {
        const latest = stateRef.current;
        const area = areaDraft(latest, latest.pageNumber, latest.areaIndex);
        if (area.polygonClosed) return;
        const first = area.polygon[0];
        if (
          area.polygon.length >= MIN_POLYGON_POINTS &&
          first !== undefined &&
          Math.hypot(point.x - first.x, point.y - first.y) <= CLOSE_RADIUS
        ) {
          finishArea();
          return;
        }
        updateArea(latest.pageNumber, latest.areaIndex, (draft) => ({ ...draft, polygon: [...draft.polygon, point] }));
      }
    },
    [mode, finishArea, updatePage, updateArea],
  );

  const undoPoint = useCallback(() => {
    updateArea(state.pageNumber, state.areaIndex, (area) => ({
      ...area,
      polygon: area.polygon.slice(0, -1),
      polygonClosed: false,
    }));
  }, [state.pageNumber, state.areaIndex, updateArea]);

  const drawArea = useCallback(
    (areaIndex: number) => {
      patch({ areaIndex });
      updateArea(state.pageNumber, areaIndex, (area) => ({ ...area, polygon: [], polygonClosed: false }));
      setMode("draw");
    },
    [patch, updateArea, state.pageNumber],
  );

  const clearArea = useCallback(
    (areaIndex: number) => {
      updateArea(state.pageNumber, areaIndex, () => EMPTY_AREA);
      setResults((current) => {
        const { [areaKey(state.pageNumber, areaIndex)]: _dropped, ...rest } = current;
        return rest;
      });
    },
    [state.pageNumber, updateArea],
  );

  // ---- recognition ----------------------------------------------------------------------

  const recognise = useCallback(async () => {
    const file = sourceFile.current;
    if (source === null || file === null) return;
    const todo = areasToRecognise(state);
    if (todo.length === 0) return;

    setProblem(null);
    cancel.current?.abort();
    const controller = new AbortController();
    cancel.current = controller;
    for (const ref of todo) {
      updateArea(ref.pageNumber, ref.areaIndex, (area) => ({ ...area, jobPhase: "submitting" }), true);
    }
    patch({ ...CLEARED_PACKAGE });

    const failures: string[] = [];
    try {
      if (sourceKey.current === null) {
        setUploadFraction(0);
        setProgress("Uploading the plan");
        sourceKey.current = await uploadPlan(file, source.contentType, setUploadFraction, headers);
        setUploadFraction(null);
      }
      const key = sourceKey.current;

      // Every job is submitted before any is awaited: the queue decides how many run at once,
      // so a six-floor project costs about one recognition's wall time per worker, not six.
      let submitted = 0;
      const watching: Promise<string | null>[] = [];
      for (const ref of todo) {
        const scale = effectiveScale(state, ref.pageNumber);
        const draft = areaDraft(state, ref.pageNumber, ref.areaIndex);
        if (scale === null) continue;
        const preview = await previewOf(ref.pageNumber);
        try {
          const job = await createJob(
            {
              sourceKey: key,
              isPdf: source.kind === "pdf",
              pageNumber: ref.pageNumber,
              outputType: state.outputType,
              inchesPerPreviewPixel: scale.scale,
              previewWidthPixels: preview.width,
              previewHeightPixels: preview.height,
              polygon: draft.polygon,
            },
            headers,
          );
          submitted += 1;
          setProgress(`Submitted ${submitted} of ${todo.length} areas`);
          watching.push(watchJob(ref, job.jobId, state.outputType, scale.scale, draft.polygon, controller.signal));
        } catch (error) {
          updateArea(ref.pageNumber, ref.areaIndex, (area) => ({ ...area, jobPhase: "failed" }), true);
          failures.push(
            `Page ${ref.pageNumber}, area ${ref.areaIndex + 1}: ${error instanceof ApiError ? error.errors.join("; ") : (error as Error).message}`,
          );
        }
      }
      const outcomes = await Promise.all(watching);
      failures.push(...outcomes.filter((message): message is string => message !== null));
    } catch (error) {
      setUploadFraction(null);
      for (const ref of todo) {
        updateArea(ref.pageNumber, ref.areaIndex, (area) => (area.jobPhase === "submitting" ? { ...area, jobPhase: "failed" } : area), true);
      }
      failures.push(error instanceof ApiError ? error.errors.join("; ") : (error as Error).message);
    }
    setProgress(null);
    if (failures.length > 0) setProblem(failures);
  }, [source, state, patch, updateArea, headers, previewOf, watchJob]);

  // ---- packaging -------------------------------------------------------------------------

  /**
   * Step 8. Creates what the drafter takes away.
   *
   * X1: each area is packaged by the API on its own, which keeps the review and scale checks
   * per result, and the finished pages are joined here into one project package - so the file
   * is a same-origin blob that does not expire. EPIC never reaches the API: it is a PNG per
   * area drawn from geometry this browser already holds; see epic-map-render.ts.
   */
  const makePackage = useCallback(async () => {
    if (source === null) return;
    const inPlay = areasInPlay(state);
    const details = exportDetails(state.details);

    setProblem(null);

    // The Save dialog needs the click's user gesture, which a multi-area package outlives, so it
    // opens first and the file is written once the package exists. An area that cannot be
    // packaged is refused before it, so nobody picks a folder for a file that will not be made.
    let target: SaveTarget | null = null;
    if (state.outputType === "DwgFloorPlan" && canChooseSaveLocation()) {
      const notReady = inPlay.find((ref) => {
        const owner = effectiveScale(state, ref.pageNumber);
        const calibration = owner === null ? null : pageSetup(state, owner.sourcePage).calibration;
        return areaDraft(state, ref.pageNumber, ref.areaIndex).run === null || calibration === null || calibration.from === null || calibration.to === null;
      });
      if (notReady !== undefined) {
        setProblem([`Page ${notReady.pageNumber}, area ${notReady.areaIndex + 1} is not ready to package.`]);
        return;
      }
      try {
        target = await chooseSaveTarget(packageFileName(details.schoolName));
      } catch (error) {
        setProblem([(error as Error).message]);
        return;
      }
      if (target === null) return;
    }

    patch({ packagePhase: "creating" });
    try {
      if (state.outputType === "EpicMap") {
        const multiple = inPlay.length > 1;
        const maps: CreatedPackage[] = [];
        for (const ref of inPlay) {
          const result = results[ref.key];
          if (result === undefined) throw new Error(`Page ${ref.pageNumber}, area ${ref.areaIndex + 1} has no geometry to draw.`);
          const epic = await buildEpicMap(result, areaDraft(state, ref.pageNumber, ref.areaIndex).polygon, await previewOf(ref.pageNumber));
          const base = epicMapFileName(details.schoolName).replace(/\.png$/, "");
          maps.push({
            kind: "epic",
            fileName: multiple ? `${base} - Page ${ref.pageNumber} Area ${ref.areaIndex + 1}.png` : `${base}.png`,
            url: URL.createObjectURL(epic.blob),
            reviewedBy: "",
          });
        }
        setState((current) => ({ ...current, packagePhase: "created", createdPackages: maps }));
        return;
      }

      const parts: AreaPackage[] = [];
      for (const ref of inPlay) {
        const draft = areaDraft(state, ref.pageNumber, ref.areaIndex);
        const scale = effectiveScale(state, ref.pageNumber);
        const owner = scale === null ? null : pageSetup(state, scale.sourcePage).calibration;
        if (draft.run === null || scale === null || owner === null || owner.from === null || owner.to === null) {
          throw new Error(`Page ${ref.pageNumber}, area ${ref.areaIndex + 1} is not ready to package.`);
        }
        const created = await createPackage(
          draft.run.jobId,
          {
            ...details,
            sourceFileName: source.fileName,
            calibrationPoint1: owner.from,
            calibrationPoint2: owner.to,
            enteredDistance: owner.distanceText,
            reviewAcknowledged: true,
          },
          headers,
        );
        parts.push({
          package: await fetchPackage(created.url),
          pageNumber: ref.pageNumber,
          areaIndex: ref.areaIndex,
          inheritsCalibrationFromPage: scale.sourcePage === ref.pageNumber ? null : scale.sourcePage,
          skipKeyplan: pageSetup(state, ref.pageNumber).skipKeyplan,
        });
      }

      const project = buildProjectPackage(parts, { state: details.state, country: details.country });
      const blob = new Blob([JSON.stringify(project, null, 2)], { type: "application/json" });
      if (target !== null) await target.write(blob);
      setState((current) => ({
        ...current,
        packagePhase: "created",
        createdPackages: [
          { kind: "x1", saved: target !== null, fileName: packageFileName(details.schoolName), url: URL.createObjectURL(blob), reviewedBy: project.reviewedBy },
        ],
      }));
    } catch (error) {
      patch({ packagePhase: "failed", createdPackages: [] });
      setProblem(error instanceof ApiError ? [...error.errors] : [(error as Error).message]);
    }
  }, [source, state, results, headers, patch, previewOf]);

  // A blob URL pins its Blob in memory until it is revoked, and an operator who renders a few
  // packages while adjusting would otherwise leave every previous one alive for the life of the
  // tab. Revoked when a list is replaced, because the old URLs are unreachable by then.
  const previousUrls = useRef<readonly string[]>([]);
  useEffect(() => {
    const current = state.createdPackages.map((p) => p.url);
    for (const url of previousUrls.current) if (!current.includes(url)) URL.revokeObjectURL(url);
    previousUrls.current = current;
  }, [state.createdPackages]);

  const blocked = blockedReason(state, source);
  const packageBlocked = packageBlockedReason(state);
  const fit = () => page !== null && setZoom(fitZoom(page.preview.width, page.preview.height));
  const warning = Object.values(results).map(recognitionWarningOf).find((text) => text !== null) ?? null;

  // Nothing is rendered behind the sign-in screen. Showing the workflow to somebody who cannot
  // use it invites them to fill it in and lose the work at the first request.
  if (auth.kind === "loading") return <div className="booting">Checking your sign-in...</div>;
  if (auth.kind === "signed-out") {
    return <SignIn reason={auth.reason} onSignIn={() => void signIn()} />;
  }

  return (
    <div
      className="app"
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault();
        const files = event.dataTransfer.files;
        if (files.length > 1) {
          setProblem(["Drop one source file at a time."]);
          return;
        }
        const file = files[0];
        if (file !== undefined) handleFile(file);
      }}
    >
      <Sidebar
        user={auth.kind === "signed-in" ? auth.user : null}
        onSignOut={() => {
          signOut();
          setAuth({ kind: "signed-out" });
        }}
        state={state}
        source={source}
        page={page}
        mode={mode}
        zoom={zoom}
        results={results}
        showResult={showResult}
        blocked={blocked}
        packageBlocked={packageBlocked}
        warning={warning}
        problem={problem}
        uploadFraction={uploadFraction}
        progress={progress}
        projects={projects}
        projectId={projectId}
        onPatch={patch}
        onDetails={setDetails}
        onFile={handleFile}
        onMode={setMode}
        onZoom={setZoom}
        onGoToPage={(n) => void goToPage(n)}
        onPage={updatePage}
        onFloorCount={setFloorCount}
        onSelectArea={(areaIndex) => patch({ areaIndex })}
        onDrawArea={drawArea}
        onClearArea={clearArea}
        onFinishArea={finishArea}
        onUndoPoint={undoPoint}
        onToggleResult={() => setShowResult((v) => !v)}
        onRecognise={() => void recognise()}
        onCreatePackage={() => void makePackage()}
        onFit={fit}
        onNewProject={newProject}
        onOpenProject={(id) => void openProject(id)}
        onDeleteProject={(id) => void removeProject(id)}
      />

      <main className="stage">
        <PlanCanvas
          page={page}
          state={state}
          mode={mode}
          results={results}
          showResult={showResult}
          zoom={zoom}
          onZoomChange={setZoom}
          onPlacePoint={placePoint}
          onFinishArea={finishArea}
          onUndoPoint={undoPoint}
          onExitMode={() => setMode("pan")}
          onFit={fit}
        />
      </main>
    </div>
  );
}

/**
 * The details as they are written into the package: the desktop application re-expands the
 * abbreviations at export, in case the operator never left a field, and normalises the state.
 */
function exportDetails(details: SchoolDetails) {
  const state = normalizeState(details.state);
  return {
    schoolName: expandDistrictAbbreviations(expandSchoolTypeAbbreviations(details.schoolName.trim()), state),
    schoolAddress: details.schoolAddress.trim(),
    districtName: expandDistrictAbbreviations(details.districtName.trim(), state),
    districtAddress: details.districtAddress.trim(),
    portalId: details.portalId.trim(),
    state,
    country: details.country,
  };
}

/** Kept local so App does not import the whole auth surface for one field. */
function userOf(session: import("@plan-forge/entra-auth").Session) {
  return {
    name: session.claims?.name ?? null,
    username: session.claims?.preferred_username ?? null,
    roles: session.claims?.roles ?? [],
  };
}

/** A first zoom that puts a whole sheet on screen, which is where every session starts. */
function fitZoom(width: number, height: number): number {
  const available = { width: window.innerWidth - 350 - 48, height: window.innerHeight - 48 };
  // Floored at the canvas's own minimum: measured while the pane was hidden, the window is
  // smaller than the sidebar and the quotient goes negative.
  return Math.max(0.05, Math.min(available.width / width, available.height / height, 1));
}
